/**
 * Célula de planilha mesclada (EU-654 / EU-765):
 *   - texto puro;
 *   - hyperlink com rótulo (Currículo / Laudo — RN-17);
 *   - `percent`: número 0..1 exibido como `0%` — célula NUMÉRICA, para a
 *     planilha ORDENAR pela grandeza (EXP-037); texto "75%" ordenaria como
 *     string.
 */
export type MergedSheetCell =
  | string
  | { text: string; hyperlink: string }
  | { kind: 'percent'; value: number }
  /**
   * DEV-268 — número com formato próprio (moeda `#,##0.00`, contagem `0`):
   * célula NUMÉRICA de verdade, para a planilha somar/ordenar e para a leitura
   * de DEV-276 receber um número, não um texto.
   */
  | { kind: 'number'; value: number; numFmt?: string };

/** Texto que a célula exibe — o que um leitor humano (ou um teste) vê. */
export function mergedSheetCellText(cell: MergedSheetCell): string {
  if (typeof cell === 'string') return cell;
  if ('hyperlink' in cell) return cell.text;
  if (cell.kind === 'number') return String(cell.value);
  return `${Math.round(cell.value * 100)}%`;
}

/**
 * Como a célula entra no ExcelJS — compartilhado pelo writer streaming (Core)
 * e pelo download no browser (SPA), para os dois produzirem a mesma planilha.
 * `value` numérico + `numFmt` é o que faz a Nota ser número de verdade.
 */
export function mergedSheetCellValue(cell: MergedSheetCell): {
  value: string | number | { text: string; hyperlink: string };
  numFmt?: string;
  hyperlink?: boolean;
} {
  if (typeof cell === 'string') return { value: cell };
  if ('hyperlink' in cell) {
    return { value: { text: cell.text, hyperlink: cell.hyperlink }, hyperlink: true };
  }
  if (cell.kind === 'number') {
    return cell.numFmt ? { value: cell.value, numFmt: cell.numFmt } : { value: cell.value };
  }
  return { value: cell.value, numFmt: '0%' };
}

/** Matriz de UM cabeçalho (bases de respostas por programa e por etapa). */
export interface SheetMatrix {
  header: string[];
  rows: string[][];
}

/**
 * Matriz de DOIS cabeçalhos (base Completa, EU-654 RN-14): linha 1 = grupos
 * mesclados, linha 2 = colunas, dados a partir da linha 3.
 */
export interface MergedSheetMatrix {
  groups: { title: string; span: number }[];
  header: string[];
  rows: MergedSheetCell[][];
}

/** Slug ASCII seguro para nome de arquivo a partir de um texto livre. */
export function fileNameSlug(text: string, fallback: string): string {
  const slug = text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return slug || fallback;
}
